import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, Upload, File, Image, FileText, Archive, X, Eye, Download } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { env } from "@/lib/environment";

interface AttachmentFile {
  id: string;
  file?: File; // Only for new uploads
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
  url?: string; // For preview or download
  serverId?: string; // For server-provided attachment ID
  // Server fields for existing attachments
  attachment_id?: number;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  upload_date?: string;
  storage_path?: string;
  isExisting?: boolean; // Flag to distinguish existing vs new files
}

interface AttachmentUploadProps {
  attachments: AttachmentFile[];
  onAttachmentsChange: (attachments: AttachmentFile[]) => void;
  maxFileSize?: number; // in bytes
  maxTotalSize?: number; // in bytes
  maxFiles?: number;
  allowedTypes?: string[];
  disabled?: boolean;
  showExisting?: boolean; // Whether to fetch and show existing attachments
}

const AttachmentUpload: React.FC<AttachmentUploadProps> = ({
  attachments,
  onAttachmentsChange,
  maxFileSize = 25 * 1024 * 1024, // 25MB default
  maxTotalSize = 100 * 1024 * 1024, // 100MB default
  maxFiles = 10,
  allowedTypes = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain', 'text/csv',
    'application/zip', 'application/x-zip-compressed'
  ],
  disabled = false,
  showExisting = true
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<AttachmentFile | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  // Fetch existing attachments
  const { data: existingAttachments, isLoading: isLoadingExisting, refetch: refetchAttachments } = useQuery({
    queryKey: ["/api/attachments/"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/attachments/");
      if (!response.ok) {
        throw new Error("Failed to fetch attachments");
      }
      const data = await response.json();
      return data.attachments || [];
    },
    enabled: showExisting && !!user,
    staleTime: 30000, // 30 seconds
  });

  // Merge existing attachments with current attachments when they load
  useEffect(() => {
    if (existingAttachments && showExisting) {
      const existingAsAttachmentFiles: AttachmentFile[] = existingAttachments.map((existing: any) => ({
        id: `existing-${existing.id}`,
        name: existing.file_name,
        size: existing.file_size,
        type: existing.file_type,
        progress: 100,
        status: 'completed' as const,
        serverId: existing.id.toString(),
        attachment_id: existing.id,
        file_name: existing.file_name,
        file_size: existing.file_size,
        file_type: existing.file_type,
        upload_date: existing.upload_date,
        storage_path: existing.storage_path,
        isExisting: true
      }));

      // Only include existing files that aren't already in the current attachments
      const currentServerIds = new Set(
        attachments
          .filter(att => att.serverId)
          .map(att => att.serverId)
      );

      const newExistingFiles = existingAsAttachmentFiles.filter(
        existing => !currentServerIds.has(existing.serverId)
      );

      if (newExistingFiles.length > 0) {
        onAttachmentsChange([...attachments, ...newExistingFiles]);
      }
    }
  }, [existingAttachments, showExisting]);

  // Note: Removed attachment and onAttachmentsChange from dependency array to prevent infinite loops
  // The component will properly update when attachments change through props

  // Check authentication on component mount
  useEffect(() => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId || !user) {
      setAuthError('Please log in to upload files');
    } else {
      setAuthError(null);
    }
  }, [user]);

  const checkAuth = (): boolean => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId || !user) {
      setAuthError('Please log in to upload files');
      toast({
        title: "Authentication required",
        description: "Please log in to upload files",
        variant: "destructive",
      });
      return false;
    }
    setAuthError(null);
    return true;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500" />;
    if (type === 'application/pdf') return <FileText className="w-4 h-4 text-red-500" />;
    if (type.includes('word') || type.includes('document')) return <FileText className="w-4 h-4 text-blue-600" />;
    if (type.includes('sheet') || type.includes('excel')) return <FileText className="w-4 h-4 text-green-600" />;
    if (type.includes('zip') || type.includes('archive')) return <Archive className="w-4 h-4 text-purple-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return `File type ${file.type} is not allowed`;
    }
    if (file.size > maxFileSize) {
      return `File size exceeds ${formatFileSize(maxFileSize)} limit`;
    }
    return null;
  };

  const validateTotalSize = (files: File[]): string | null => {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const currentSize = attachments.reduce((sum, att) => sum + att.size, 0);
    if (totalSize + currentSize > maxTotalSize) {
      return `Total size exceeds ${formatFileSize(maxTotalSize)} limit`;
    }
    return null;
  };

  const uploadFile = useCallback(async (fileId: string, delay: number = 0, attachmentFile?: AttachmentFile) => {
    // Add delay for staggered uploads
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    // Use passed attachment or find it in current attachments
    const attachment = attachmentFile || attachments.find(att => att.id === fileId);
    if (!attachment || !attachment.file) {
      console.error(`Attachment with ID ${fileId} not found or has no file`);
      return;
    }

    const updateProgress = (progress: number, status: 'uploading' | 'completed' | 'error' = 'uploading') => {
      // Use the attachment parameter directly to avoid closure issues
      const updatedAttachments = attachments.map(att => 
        att.id === fileId 
          ? { ...att, progress, status }
          : att
      );
      onAttachmentsChange(updatedAttachments);
    };

    try {
      // Create FormData for file upload
      const formData = new FormData();
      if (attachment.file) {
        formData.append('files', attachment.file);
      } else {
        throw new Error('No file data available for upload');
      }

      // Get session token
      const sessionId = localStorage.getItem('sessionId');
      if (!sessionId) {
        throw new Error('Not authenticated - please log in again');
      }

      updateProgress(10);

      // Upload to backend
      const response = await fetch(`${env.apiUrl}/api/attachments/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          // Don't set Content-Type header - let the browser set it with boundary for multipart
        },
        body: formData,
      });

      updateProgress(70);

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}`;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      updateProgress(100, 'completed');

      console.log('File uploaded successfully:', result);
      console.log('Current fileId:', fileId);
      
      // Extract attachment_id from server response - the server returns { files: [{ id: X, ... }] }
      let attachmentId = null;
      if (result.files && result.files.length > 0) {
        // The server returns { files: [{ id: X, filename: Y, ... }] }
        attachmentId = result.files[0].id;
      }
      
      console.log('Extracted attachment_id:', attachmentId);
      
      // Update attachment with server response - fix closure issue by getting current attachments
      const currentAttachments = attachments.filter(att => att.id !== fileId);
      const updatedAttachment = {
        ...attachment,
        progress: 100,
        status: 'completed' as const,
        serverId: attachmentId?.toString(),
        attachment_id: attachmentId,
        file_name: result.files?.[0]?.filename || attachment.name,
        file_size: result.files?.[0]?.size || attachment.size,
        file_type: result.files?.[0]?.type || attachment.type,
        upload_date: result.files?.[0]?.upload_date
      };
      
      const updatedAttachments = [...currentAttachments, updatedAttachment];
      
      console.log('Updated attachments array:', updatedAttachments);
      console.log('Updated attachment after upload:', updatedAttachment);
      
      onAttachmentsChange(updatedAttachments);

      // Refetch existing attachments to keep the list updated
      if (showExisting) {
        refetchAttachments();
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      
      // Update attachment with error
      const updatedAttachments = attachments.map(att => 
        att.id === fileId 
          ? { ...att, status: 'error' as const, error: errorMessage }
          : att
      );
      onAttachmentsChange(updatedAttachments);
      
      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [attachments, onAttachmentsChange, toast, showExisting, refetchAttachments]);

  const processFiles = useCallback((files: FileList | File[]) => {
    // Check authentication first
    if (!checkAuth()) {
      return;
    }

    const fileArray = Array.from(files);
    
    // Check max files limit (excluding existing files)
    const newFileCount = attachments.filter(att => !att.isExisting).length;
    if (newFileCount + fileArray.length > maxFiles) {
      toast({
        title: "Too many files",
        description: `Maximum ${maxFiles} files allowed`,
        variant: "destructive",
      });
      return;
    }

    // Validate total size
    const totalSizeError = validateTotalSize(fileArray);
    if (totalSizeError) {
      toast({
        title: "Size limit exceeded",
        description: totalSizeError,
        variant: "destructive",
      });
      return;
    }

    // Process each file
    const newAttachments: AttachmentFile[] = [];
    const errors: string[] = [];

    fileArray.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        const attachment: AttachmentFile = {
          id: Math.random().toString(36).substring(2, 11),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          progress: 0,
          status: 'uploading',
          isExisting: false
        };
        newAttachments.push(attachment);
      }
    });

    // Show validation errors
    if (errors.length > 0) {
      toast({
        title: "File validation errors",
        description: errors.join(', '),
        variant: "destructive",
      });
    }

    // Add valid files
    if (newAttachments.length > 0) {
      const updatedAttachments = [...attachments, ...newAttachments];
      onAttachmentsChange(updatedAttachments);
      
      // Start uploads with staggered delay
      newAttachments.forEach((attachment, index) => {
        uploadFile(attachment.id, index * 200, attachment); // Stagger uploads, pass attachment directly
      });
    }
  }, [attachments, onAttachmentsChange, maxFiles, maxFileSize, maxTotalSize, allowedTypes, toast, uploadFile, checkAuth]);

  const removeAttachment = async (fileId: string) => {
    const attachment = attachments.find(att => att.id === fileId);
    
    // If it's an existing attachment, delete it from the server
    if (attachment?.isExisting && attachment.attachment_id) {
      try {
        const response = await apiRequest("DELETE", `/api/attachments/${attachment.attachment_id}`);
        if (!response.ok) {
          throw new Error("Failed to delete attachment");
        }
        
        toast({
          title: "Success",
          description: "Attachment deleted successfully",
        });
        
        // Refetch existing attachments
        if (showExisting) {
          refetchAttachments();
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete attachment",
          variant: "destructive",
        });
        return; // Don't remove from UI if server deletion failed
      }
    }
    
    // Remove from local state
    onAttachmentsChange(attachments.filter(att => att.id !== fileId));
  };

  const handlePreview = async (attachment: AttachmentFile) => {
    try {
      setIsLoadingPreview(true);
      
      // Get the attachment ID - prefer attachment_id, fall back to serverId
      const attachmentId = attachment.attachment_id || attachment.serverId;
      if (!attachmentId) {
        throw new Error('No attachment ID available for preview');
      }
      
      console.log('Previewing attachment:', { id: attachmentId, name: attachment.name });
      
      // Get signed URL for preview
      const sessionId = localStorage.getItem('sessionId');
      
      const response = await fetch(`${env.apiUrl}/api/attachments/${attachmentId}/url?expires_in=3600`, {
        headers: {
          'Authorization': `Bearer ${sessionId}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to get preview URL');
      }
      
      const result = await response.json();
      
      setPreviewFile({
        ...attachment,
        url: result.signed_url
      });
      
    } catch (error) {
      console.error('Preview error:', error);
      toast({
        title: "Preview failed",
        description: error instanceof Error ? error.message : "Could not preview file",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDownload = async (attachment: AttachmentFile) => {
    try {
      // Get the attachment ID - prefer attachment_id, fall back to serverId
      const attachmentId = attachment.attachment_id || attachment.serverId;
      if (!attachmentId) {
        throw new Error('No attachment ID available for download');
      }
      
      console.log('Downloading attachment:', { id: attachmentId, name: attachment.name });
      
      const sessionId = localStorage.getItem('sessionId');
      
      const response = await fetch(`${env.apiUrl}/api/attachments/${attachmentId}/download`, {
        headers: {
          'Authorization': `Bearer ${sessionId}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "Could not download file",
        variant: "destructive",
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled || authError) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    if (!checkAuth()) {
      return;
    }
    fileInputRef.current?.click();
  };

  const getTotalSize = () => {
    return attachments.reduce((sum, att) => sum + att.size, 0);
  };

  const getNewFilesCount = () => {
    return attachments.filter(att => !att.isExisting).length;
  };

  return (
    <div className="w-full space-y-2">
      {/* Authentication Error Alert */}
      {authError && (
        <Alert variant="destructive">
          <AlertDescription>{authError}</AlertDescription>
        </Alert>
      )}

      {/* Loading indicator for existing attachments */}
      {isLoadingExisting && showExisting && (
        <div className="text-sm text-gray-500 text-center py-2">
          Loading existing attachments...
        </div>
      )}

      {/* Upload Zone */}
      <Card className={`border-2 border-dashed transition-colors ${
        isDragOver 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-gray-300 hover:border-gray-400'
      } ${disabled || authError ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
        <CardContent 
          className="p-2 text-center"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={disabled || authError ? undefined : handleUploadClick}
        >
          <div className="flex flex-col items-center space-y-0.5">
            <Upload className={`w-4 h-4 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
            <div className="text-xs text-gray-600">
              <span className="font-medium">
                {authError ? 'Authentication required' : 'Click to upload'}
              </span> or drag and drop
            </div>
            <div className="text-xs text-gray-500">
              Max {formatFileSize(maxFileSize)} per file
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={allowedTypes.join(',')}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || !!authError}
      />

      {/* File List */}
      {attachments.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs font-medium text-gray-700">
              Attachments ({attachments.length}) {showExisting && `• New: ${getNewFilesCount()}`}
            </h4>
            <Badge variant="outline" className="text-xs px-1 py-0">
              {formatFileSize(getTotalSize())} / {formatFileSize(maxTotalSize)}
            </Badge>
          </div>
          
          {attachments.map((attachment) => (
            <Card key={attachment.id} className="p-2">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  {getFileIcon(attachment.type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {attachment.name}
                    </p>
                    <div className="flex items-center space-x-0.5">
                      {/* Show preview and download buttons for completed attachments */}
                      {(attachment.status === 'completed' && (attachment.attachment_id || attachment.serverId)) && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(attachment)}
                            className="h-5 w-5 p-0 hover:bg-blue-100"
                            disabled={disabled || isLoadingPreview}
                            title="Preview file"
                          >
                            <Eye className="w-3 h-3 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(attachment)}
                            className="h-5 w-5 p-0 hover:bg-green-100"
                            disabled={disabled}
                            title="Download file"
                          >
                            <Download className="w-3 h-3 text-green-500" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(attachment.id)}
                        className="h-5 w-5 p-0 hover:bg-red-100"
                        disabled={disabled}
                        title="Remove attachment"
                      >
                        <X className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 mt-0.5">
                    <span className="text-xs text-gray-500">
                      {formatFileSize(attachment.size)}
                    </span>
                    {attachment.isExisting && (
                      <Badge variant="secondary" className="text-xs px-1 py-0">
                        Existing
                      </Badge>
                    )}
                    {attachment.status === 'uploading' && (
                      <>
                        <span className="text-xs text-blue-600">
                          {Math.round(attachment.progress)}%
                        </span>
                        <Progress value={attachment.progress} className="h-1 flex-1" />
                      </>
                    )}
                    {attachment.status === 'completed' && !attachment.isExisting && (
                      <Badge variant="secondary" className="text-xs px-1 py-0">
                        Ready
                      </Badge>
                    )}
                    {attachment.status === 'error' && (
                      <Badge variant="destructive" className="text-xs px-1 py-0">
                        Error
                      </Badge>
                    )}
                  </div>
                  
                  {attachment.error && (
                    <Alert className="mt-1 py-1">
                      <AlertDescription className="text-xs">
                        {attachment.error}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Summary */}
      {attachments.length > 0 && (
        <div className="text-xs text-gray-500 flex justify-between items-center">
          <span>
            {attachments.filter(a => a.status === 'completed').length} of {attachments.length} files ready
          </span>
          <span>
            Total size: {formatFileSize(getTotalSize())}
          </span>
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={() => {
        setPreviewFile(null);
        setPreviewUrl(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Preview: {previewFile?.name}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {isLoadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-500">Loading preview...</div>
              </div>
            ) : previewFile?.url ? ( // Changed from previewUrl to previewFile?.url
              <div className="space-y-4">
                {previewFile?.type.startsWith('image/') ? (
                  <img 
                    src={previewFile.url} 
                    alt={previewFile.name}
                    className="max-w-full h-auto rounded-lg"
                  />
                ) : previewFile?.type === 'application/pdf' ? (
                  <iframe 
                    src={previewFile.url}
                    className="w-full h-96 border rounded-lg"
                    title={previewFile.name}
                  />
                ) : previewFile?.type.startsWith('text/') ? (
                  <iframe 
                    src={previewFile.url}
                    className="w-full h-96 border rounded-lg"
                    title={previewFile.name}
                  />
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-sm text-gray-500 mb-4">
                      Preview not available for this file type
                    </p>
                    <Button onClick={() => previewFile && handleDownload(previewFile)}>
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                  </div>
                )}
                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    {formatFileSize(previewFile?.size || 0)} • {previewFile?.type}
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => previewFile && handleDownload(previewFile)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-sm text-gray-500">Failed to load preview</div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttachmentUpload; 